// Copyright (c) 2026 Flower Ave, Inc.

export interface LifecycleCallbacks {
    onAppear?: () => void;
    onDisappear?: () => void;
    onForeground?: () => void;
    onBackground?: () => void;
    onGamesTabSelected?: () => void;
    onGamesTabLeft?: () => void;
}

type LifecycleEvent = 'onAppear' | 'onDisappear' | 'onForeground' | 'onBackground' | 'onGamesTabSelected' | 'onGamesTabLeft';

// Games listen to `lifecycle-pause` to auto-pause on background / tab-leave.
// Dispatched once per native event, independent of how many components
// subscribe via `initLifecycleListeners`.
if (typeof window !== 'undefined') {
    const dispatchPause = () => window.dispatchEvent(new Event('lifecycle-pause'));
    window.addEventListener('onBackground', dispatchPause);
    window.addEventListener('onGamesTabLeft', dispatchPause);
}

/**
 * Initialize lifecycle event listeners for iOS WKWebView integration.
 * These events are dispatched by the native iOS app to notify the web app
 * of view and app lifecycle changes. See `WebViewEvent` in the iOS app's
 * `WKWebViewHelper.swift` for the authoritative list of event names.
 */
export function initLifecycleListeners(callbacks?: LifecycleCallbacks): () => void {
    const registered: Array<[LifecycleEvent, () => void]> = [];
    const register = (event: LifecycleEvent, callback?: () => void) => {
        if (!callback) return;
        window.addEventListener(event, callback);
        registered.push([event, callback]);
    };

    register('onAppear', callbacks?.onAppear);
    register('onDisappear', callbacks?.onDisappear);
    register('onForeground', callbacks?.onForeground);
    register('onBackground', callbacks?.onBackground);
    register('onGamesTabSelected', callbacks?.onGamesTabSelected);
    register('onGamesTabLeft', callbacks?.onGamesTabLeft);

    return () => {
        for (const [event, callback] of registered) {
            window.removeEventListener(event, callback);
        }
    };
}
